import React, { useState, useEffect } from "react";
import Input from "components/BaseWidgets/form/input";
import Select from "components/BaseWidgets/select";
import { EditIcon, GreenTickCircle, HelpIcon, SaveIcon } from "assets/icons";
import Button from "components/BaseWidgets/button";
import { getContactDetails } from "./helper";
import { displayTextOrDash } from "utils/formatHelper";
import { useEditProfile } from "hooks/api/useProfile";
import { useQueryClient } from "@tanstack/react-query";
import { useSelectCode } from "hooks/api/useListing";
import { useContactRequest, useProfileCompletion } from "hooks/api/useHome";
import AlertNotification from "components/CustomWidgets/alertNotification";
import Tooltip from "components/BaseWidgets/toolTip";
import { GreenVerification } from "assets/icons";
import "../../style.scss";
import OtpModal from "components/LandingScreen/components/OtpModal";
import { useSendOtp, useVerifyOtp } from "hooks/api/useSettings";
import { useNavigate } from "react-router-dom";

const contactSelectOptions = {};

function ContactPanel({
  blurredField = [],
  isEdit,
  id,
  contactDetails,
  isLock,
  userId,
  preview,
}) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [update, setUpdate] = useState(null);
  const [updatedMail, setUpdatedMail] = useState("");
  const [updatedMobile, setUpdatedMobile] = useState("");
  const queryClient = useQueryClient();
  const { mutate: editProfile, isLoading: isUpdating } = useEditProfile();
  const { mutate: requestContact, isLoading: reqLoading } = useContactRequest({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-profile"] });
      queryClient.invalidateQueries({ queryKey: ["account_settings"] });
    },
  });
  const { data: userData } = useProfileCompletion();

  const contactData = contactDetails?.contact || {};
  const [isEmailOtpVerified, setIsEmailOtpVerified] = useState(false);
  const [isPhoneOtpVerified, setIsPhoneOtpVerified] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const formattedContactDetails = getContactDetails(contactData);

  const { data: countrylist, isLoading: isCommunityPending } = useSelectCode({
    search_key: "",
  });
  const { mutate: otpSubmit, isLoading } = useVerifyOtp({
    onSuccess: (data) => {
      if (data.success) {
        setIsModalOpen(false);
        if (update === "mail") {
          setIsEmailOtpVerified(true);
        } else {
          setIsPhoneOtpVerified(true);
        }
        updateApiCall();
      }
    },
  });

  const { mutate: sendOtp, isLoading: sendLoading } = useSendOtp({
    onSuccess: () => {
      setIsModalOpen(true);
    },
  });

  const CountryCodes = countrylist?.country_codes?.map((item) => {
    return {
      value: item.id,
      label: item.code,
      phone_length: item.phone_length,
    };
  });

  // Initialize formData
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (formattedContactDetails && formattedContactDetails.length > 0) {
      const newFormData = Object.fromEntries(
        formattedContactDetails.map((item) => [
          item.label,
          item.value === null || item.value === undefined
            ? ""
            : typeof item.value === "object"
              ? ""
              : String(item.value),
        ]),
      );
      if (contactData?.countrycode?.id) {
        newFormData["countryCode"] = contactData.countrycode.id;
      }

      setFormData(newFormData);
    }
  }, [contactDetails]);

  useEffect(() => {
    if (!formData?.countryCode || !CountryCodes?.length) return;

    const country =
      CountryCodes.find((c) => c.value === formData.countryCode) || null;

    setSelectedCountry((prev) =>
      prev?.value === country?.value ? prev : country,
    );
  }, [formData?.countryCode, CountryCodes]);

  const handleChange = (label, value) => {
    if (label === "countryCode") {
      setFormData((prev) => ({
        ...prev,
        countryCode: value,
        "Contact Number": "",
      }));
    } else {
      setFormData((prev) => ({ ...prev, [label]: value }));
    }
  };

  const getOptions = (label) => {
    return contactSelectOptions[label] || null;
  };

  const handleEditSave = () => {
    if (editing) {
      const emailValue = formData["Email ID"]?.trim();
      const phoneValue = formData["Contact Number"]?.trim();
      const emailChanged =
        emailValue !== "" && emailValue !== (contactData?.email || "").trim();
      const phoneChanged =
        phoneValue !== "" && phoneValue !== (contactData?.phone || "").trim();
      if (emailChanged && !isEmailOtpVerified) {
        AlertNotification(
          "warning",
          "Please verify your updated email before saving.",
        );
        return;
      }

      if (phoneChanged && !isPhoneOtpVerified) {
        AlertNotification(
          "warning",
          "Please verify your updated phone number before saving.",
        );
        return;
      }
      const selectedCountry = CountryCodes?.find(
        (c) => c.value === formData["countryCode"],
      );

      const payload = {
        countrycode: selectedCountry
          ? selectedCountry.value
          : contactData?.countrycode?.id,
        phone: formData["Contact Number"] || null,
        whatsapp_number: formData["Whatsapp Number"] || null,
        email: formData["Email ID"] || null,
      };

      editProfile(payload, {
        onSuccess: () => {
          setEditing(false);
          queryClient.invalidateQueries({
            queryKey: ["my-profile"],
          });
        },
      });
      if (update === "mail") {
        setIsEmailOtpVerified(false);
      } else {
        setIsPhoneOtpVerified(false);
      }
    } else {
      setEditing(true);
    }
  };

  const isEmailVerified = contactData?.email_verified;
  const isPhoneVerified = contactData?.phone_status;

  // Helper function to check verification per field
  const isFieldUnverified = (label) => {
    switch (label.toLowerCase()) {
      case "email id":
        return isEmailVerified;
      case "contact number":
        return isPhoneVerified;
      case "whatsapp number":
        return isPhoneVerified;
      default:
        return false;
    }
  };

  const isUnlock =
    contactData?.approved_contact_request_exists &&
    !contactData?.contact_request_exists &&
    !isLock;
  return (
    <div id={id} className="contact-panel-frame panel-frame">
      <div className="contact-details">
        <div className="header flex items-center justify-between">
          <label className="contact-label font-title-normal">Contact</label>
          {isEdit && (
            <Button
              variant="custom"
              onClick={handleEditSave}
              className="edit-button"
              disabled={isUpdating}
            >
              {editing ? (
                <>
                  <SaveIcon /> Save
                </>
              ) : (
                <>
                  <EditIcon /> Edit
                </>
              )}
            </Button>
          )}
        </div>
        {!contactData?.approved_contact_request_exists && !isEdit && (
          <Button
            isDisabled={contactData?.contact_request_exists}
            onClick={() => {
              if (preview) return;
              if (userData?.common_home_data?.contact_limit === 0) {
                AlertNotification(
                  "warning",
                  <>
                    Request limit reached.{" "}
                    <Button variant="link" onClick={() => navigate("/plans")}>
                      Upgrade Now
                    </Button>
                  </>,
                );
                return;
              }
              requestContact({ id: userId });
            }}
            height={36}
            className={"req-btn"}
            isLoading={reqLoading}
          >
            {contactData?.contact_request_exists ? "Requested" : "View contact"}
          </Button>
        )}

        <div
          className={`content flex flex-col gap-6 ${!isUnlock && !isEdit && "locked-card"}`}
        >
          <div className="content-grid flex flex-col gap-4">
            {!editing
              ? formattedContactDetails.map((item) => (
                  <div
                    key={item.label}
                    className="item-frame flex flex-col gap-1"
                  >
                    <span className="item-label">{item.label}</span>
                    <div className="flex items-center gap-2">
                      <span
                        className={`item-value ${
                          blurredField.includes(item.label) ? "blur-text" : ""
                        }`}
                      >
                        <div className="flex gap-1">
                          {displayTextOrDash(item.value)}
                          {item.label !== "Whatsapp Number" &&
                            isFieldUnverified(item.label) &&
                            item.value && (
                              <span className="green-tick-small">
                                <GreenTickCircle />
                              </span>
                            )}
                          {isEdit &&
                            item.value &&
                            !isFieldUnverified(item.label) && (
                              <Tooltip title="You should update your details to see this">
                                <span>
                                  <HelpIcon />
                                </span>
                              </Tooltip>
                            )}
                        </div>
                      </span>
                      {blurredField.includes(item.label) && (
                        <Tooltip title="You should update your details to see this">
                          <span className="">
                            <HelpIcon />
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                ))
              : formattedContactDetails.map((item) => {
                  if (editing && item.label === "Contact Number") {
                    const verified = isFieldUnverified(item.label);
                    return (
                      <div
                        key={item.label}
                        className="item-frame flex flex-col gap-1"
                      >
                        <div
                          className="flex items-center gap-2"
                          style={{ height: "20px" }}
                        >
                          <span className="item-label">{item.label}</span>
                          {(verified &&
                            formData[item.label].trim() &&
                            formData[item.label].trim() ===
                              (contactData?.phone || "").trim()) ||
                          isPhoneOtpVerified ? (
                            <span className="text-green-500 flex items-center gap-1 green-tick-small">
                              <GreenTickCircle />
                            </span>
                          ) : formData[item.label].trim() ? (
                            <Button
                              onClick={() => {
                                const phone = (
                                  updatedMobile || formData[item.label]
                                )?.trim();
                                const phoneRegex = /^[0-9]{7,15}$/;
                                if (!phone) {
                                  AlertNotification(
                                    "warning",
                                    "Please enter your phone before verifying.",
                                  );
                                  return;
                                }
                                if (!phoneRegex.test(phone)) {
                                  AlertNotification(
                                    "warning",
                                    "Please enter a valid phone address.",
                                  );
                                  return;
                                }
                                setUpdate("mobile");
                                sendOtp({
                                  identifier: formData[item.label],
                                });
                                setIsModalOpen(true);
                              }}
                              variant="link"
                            >
                              Verify
                            </Button>
                          ) : null}
                        </div>

                        <div className="flex gap-2 items-center">
                          <Select
                            // showSearch={false}
                            SkeletonHeight={24}
                            label={""}
                            value={
                              formData["countryCode"] ||
                              CountryCodes?.[0]?.value ||
                              ""
                            }
                            options={CountryCodes}
                            onChange={(val) => {
                              const country = CountryCodes.find(
                                (countryData) => countryData.value === val,
                              );
                              setSelectedCountry(country);
                              handleChange("countryCode", val);
                              setUpdatedCountry(val);
                            }}
                            inputClassName="item-input-select"
                          />
                          <Input
                            id={item.label}
                            label={""}
                            value={formData[item.label] || ""}
                            onChange={(e) => {
                              handleChange(item.label, e.target.value);
                              setUpdatedMobile(e.target.value);
                            }}
                            maxLength={selectedCountry?.phone_length}
                            inputClassname="item-input"
                            type="tel"
                            inputMode="numeric"
                            onKeyPress={(e) => {
                              if (!/[0-9]/.test(e.key)) e.preventDefault();
                            }}
                          />
                        </div>
                      </div>
                    );
                  }
                  const options = getOptions(item.label);
                  const verified = isFieldUnverified(item.label);
                  return (
                    <div
                      key={item.label}
                      className="item-frame flex flex-col gap-1"
                    >
                      <div
                        className="flex items-center gap-2"
                        style={{ height: "20px" }}
                      >
                        <span className="item-label">{item.label}</span>
                        {item.label !== "Whatsapp Number" &&
                        formData[item.label].trim() ? (
                          (verified &&
                            formData[item.label].trim() ===
                              (contactData?.email || "").trim()) ||
                          isEmailOtpVerified ? (
                            <span className="green-tick-small mb-2">
                              <GreenTickCircle />
                            </span>
                          ) : (
                            <Button
                              onClick={() => {
                                const email = (
                                  updatedMail || formData[item.label]
                                )?.trim();
                                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                                if (!email) {
                                  AlertNotification(
                                    "warning",
                                    "Please enter your email or phone before verifying.",
                                  );
                                  return;
                                }
                                if (!emailRegex.test(email)) {
                                  AlertNotification(
                                    "warning",
                                    "Please enter a valid email address.",
                                  );
                                  return;
                                }
                                if (item.label === "Email ID") {
                                  setUpdate("mail");
                                  sendOtp({
                                    identifier:
                                      updatedMail || contactData?.email,
                                  });
                                }
                                setIsModalOpen(true);
                              }}
                              className=""
                              variant="link"
                            >
                              Verify
                            </Button>
                          )
                        ) : null}
                      </div>
                      {options ? (
                        <Select
                          SkeletonHeight={24}
                          label={""}
                          value={formData[item.label] || ""}
                          options={options}
                          onChange={(val) => handleChange(item.label, val)}
                          inputClassName="item-input-select"
                        />
                      ) : (
                        <Input
                          id={item.label}
                          label={""}
                          value={
                            typeof formData[item.label] === "object" ||
                            formData[item.label] == null
                              ? ""
                              : formData[item.label]
                          }
                          onChange={(e) => {
                            handleChange(item.label, e.target.value);
                            setUpdatedMail(e.target.value);
                          }}
                          inputClassname="item-input"
                          type={
                            item.label === "Whatsapp Number" ? "tel" : "text"
                          }
                          inputMode={
                            item.label === "Whatsapp Number"
                              ? "numeric"
                              : undefined
                          }
                          onKeyPress={(e) => {
                            if (
                              item.label === "Whatsapp Number" &&
                              !/[0-9]/.test(e.key)
                            ) {
                              e.preventDefault();
                            }
                          }}
                        />
                      )}
                    </div>
                  );
                })}
          </div>
        </div>

        <OtpModal
          // isLoading={isLoading}
          open={isModalOpen}
          onCancel={() => {
            setIsModalOpen(false);
          }}
          title="OTP Verification"
          formData={{
            contact:
              update === "mail"
                ? updatedMail || contactData?.email
                : updatedMobile || contactData?.phone,
          }}
          onSubmit={(payload) => {
            otpSubmit(payload);
          }}
          onResend={() => {
            sendOtp({
              identifier:
                update === "mail"
                  ? updatedMail || contactData?.email
                  : updatedMobile || contactData?.phone,
            });
          }}
        />
      </div>
    </div>
  );
}

export default ContactPanel;
